import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { Loader, Text } from '@cash-me/react-components'
import NextLink from 'next/link'
import Script from 'next/script'

import { RDStationFormsWrapper } from 'components/general-styles/RDStationForm'
import { Modal, type ModalRefsProps } from 'components/modal'

import * as S from './styles'

const FORM_HASH = 'UA-115666087-1'
const FORM_ID = 'cashme-condo-hotsite-f80dc8a8a349497537bb'
const FORM_WRAPPER_ID = 'condominiumCreditForm'

// Se o envio falhar sem trocar o conteúdo do formulário, o overlay ficaria
// cobrindo tudo indefinidamente — o usuário veria o erro do RD Station sem
// conseguir interagir para corrigi-lo. Este é o limite para liberá-lo.
const SUBMIT_TIMEOUT_MS = 15_000

const CondominiumCreditFormComponent = forwardRef<ModalRefsProps>((_, ref) => {
  const modalRef = useRef<ModalRefsProps>(null)
  const hasCreatedFormRef = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasScriptError, setHasScriptError] = useState(false)

  const createRDStationForm = useCallback(() => {
    const RDStationForms = window['RDStationForms']

    if (RDStationForms && !hasCreatedFormRef.current) {
      hasCreatedFormRef.current = true
      new RDStationForms(FORM_ID, FORM_HASH).createForm()
    }
  }, [])

  const handleScriptError = useCallback(() => setHasScriptError(true), [])

  const handleSubmit = useCallback(() => {
    const form = document.querySelector(`#${FORM_WRAPPER_ID}`)?.querySelector('form')

    // A validação é delegada ao próprio formulário: todo campo obrigatório do RD
    // Station já vem com `required`, então `checkValidity()` cobre presença e
    // formato sem replicar a lista de campos aqui. Replicá-la acoplaria a
    // validação a nomes como `custom_fields[886093]`, que mudam quando o campo é
    // recriado no builder — quebrando em silêncio, sem erro nem teste falhando.
    if (form?.checkValidity()) setIsSubmitting(true)
  }, [])

  useImperativeHandle(ref, () => ({
    openModal() {
      setIsSubmitting(false)
      modalRef.current?.openModal()
    },
    closeModal() {
      modalRef.current?.closeModal?.()
    }
  }))

  useEffect(() => {
    createRDStationForm()
  }, [createRDStationForm])

  useEffect(() => {
    if (!isSubmitting) return undefined

    const timeoutId = setTimeout(() => setIsSubmitting(false), SUBMIT_TIMEOUT_MS)

    return () => clearTimeout(timeoutId)
  }, [isSubmitting])

  useEffect(() => {
    const wrapper = document.querySelector(`#${FORM_WRAPPER_ID}`)

    if (!wrapper) return undefined

    let submitButton: HTMLButtonElement | null = null

    const attachSubmitListener = () => {
      const nextSubmitButton = wrapper.querySelector<HTMLButtonElement>('form button')

      if (nextSubmitButton === submitButton) return

      submitButton?.removeEventListener('click', handleSubmit)
      submitButton = nextSubmitButton
      submitButton?.addEventListener('click', handleSubmit)
    }

    const observer = new MutationObserver(attachSubmitListener)
    observer.observe(wrapper, { childList: true, subtree: true })
    attachSubmitListener()

    return () => {
      observer.disconnect()
      submitButton?.removeEventListener('click', handleSubmit)
    }
  }, [handleSubmit])

  return (
    <Modal ref={modalRef} title="Contate um consultor">
      {isSubmitting ? (
        <S.LoadingOverlay aria-live="polite" aria-label="Enviando solicitação">
          <Loader color="primary" size="md" loaderBackground="#fff" />
        </S.LoadingOverlay>
      ) : null}
      {hasScriptError ? (
        <S.FormFallback role="alert">
          <Text size="body2" color="var(--colors-neutral-text-default)">
            Não foi possível carregar o formulário agora.
          </Text>
          <NextLink href="/fale-conosco/" className="ds-link">
            Fale com a equipe CashMe
          </NextLink>
        </S.FormFallback>
      ) : null}
      <RDStationFormsWrapper id={FORM_WRAPPER_ID}>
        <div id={FORM_ID} />
        <Script
          id="RDStationCondominiumCredit"
          src="https://d335luupugsy2.cloudfront.net/js/rdstation-forms/stable/rdstation-forms.min.js"
          strategy="lazyOnload"
          onReady={createRDStationForm}
          onError={handleScriptError}
        />
      </RDStationFormsWrapper>
    </Modal>
  )
})

CondominiumCreditFormComponent.displayName = 'CondominiumCreditFormComponent'

export const CondominiumCreditForm = memo(CondominiumCreditFormComponent)

CondominiumCreditForm.displayName = 'CondominiumCreditForm'
